import React, { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import html2canvas from "html2canvas";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import Heatmap1 from "../components/Dashboardviz/D06/D06Viz05";
import Heatmap2 from "../components/Dashboardviz/D06/D06Viz06";
import Heatmap3 from "../components/Dashboardviz/D06/D06Viz07";
import Heatmap4 from "../components/Dashboardviz/D06/D06Viz08";
import Score01 from "../components/Dashboardviz/D06/D06Viz01";
import Score02 from "../components/Dashboardviz/D06/D06Viz02";
import Score03 from "../components/Dashboardviz/D06/D06Viz03";
import Score04 from "../components/Dashboardviz/D06/D06Viz04";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";
import "./D06_Detailed_Scores_by_Demographic_Category.css";

const D06_Detailed_Scores_by_Demographic_CategoryDashboard = () => {
  const { filterCriteria, setFilterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [selectedDemographic, setSelectedDemographic] = useState(
    "demographic_category_01"
  );
  const [filterData, setFilterData] = useState({});

  // Refs for screenshot capture (each heatmap + score section)
  const roleClarityRef = useRef(null);
  const workContextRef = useRef(null);
  const jobSatisfactionRef = useRef(null);
  const organizationalCommitmentRef = useRef(null);

  // Function to capture screenshot of a specific section
  const captureScreenshot = async (ref, fileName) => {
    if (ref.current) {
      const canvas = await html2canvas(ref.current, { scale: 2 });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${fileName}.png`;
      link.click();
    }
  };

  // Function to capture all screenshots in sequence
  const captureAllScreenshots = async () => {
    await captureScreenshot(roleClarityRef, "role-clarity");
    await captureScreenshot(workContextRef, "work-context");
    await captureScreenshot(jobSatisfactionRef, "job-satisfaction");
    await captureScreenshot(
      organizationalCommitmentRef,
      "organizational-commitment"
    );
  };

  // Generate filter options based on FILTER_CONFIG
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter(
          (value) => value !== null && value !== undefined
        );

        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues = uniqueValues.sort((a, b) =>
            a?.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });
      return options;
    };

    setFilterData(generateFilterOptions());
  }, []);

  const toggleSidebar = useCallback(
    debounce(() => setIsSidebarOpen((prev) => !prev), 300),
    []
  );

  const toggleFilterSidebar = useCallback(
    debounce(() => setIsFilterSidebarOpen((prev) => !prev), 300),
    []
  );

  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const handleFilterChange = (updatedCriteria) => {
    setFilterCriteria(updatedCriteria);
  };

  // Fix chart dimensions after rendering
  useEffect(() => {
    const fixChartDimensions = () => {
      // Make sure titles in score charts are visible
      document.querySelectorAll('.score .apexcharts-yaxis-title-text').forEach(element => {
        element.style.visibility = 'visible';
        element.style.opacity = '1';
      });
      
      // Ensure y-axis labels are visible
      document.querySelectorAll('.score .apexcharts-yaxis-label').forEach(element => {
        element.style.visibility = 'visible';
        element.style.opacity = '1';
      });
    };
    
    // Run the fix after rendering and after any state changes
    const timeout = setTimeout(fixChartDimensions, 500);
    return () => clearTimeout(timeout);
  }, [selectedDemographic, filterCriteria]);

  const chartComponents = [
    {
      heatmap: (
        <Heatmap1
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
        />
      ),
      score: (
        <div className="score-wrapper">
          <div className="score-title">Role Clarity</div>
          <div className="score-chart">
            <Score01
              selectedDemographic={selectedDemographic}
              filterCriteria={filterCriteria}
            />
          </div>
        </div>
      ),
      title: "Role Clarity",
      ref: roleClarityRef,
    },
    {
      heatmap: (
        <Heatmap2
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
        />
      ),
      score: (
        <div className="score-wrapper">
          <div className="score-title">Work Context</div>
          <div className="score-chart">
            <Score02
              selectedDemographic={selectedDemographic}
              filterCriteria={filterCriteria}
            />
          </div>
        </div>
      ),
      title: "Work Context",
      ref: workContextRef,
    },
    {
      heatmap: (
        <Heatmap3
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
        />
      ),
      score: (
        <div className="score-wrapper">
          <div className="score-title">Job Satisfaction</div>
          <div className="score-chart">
            <Score03
              selectedDemographic={selectedDemographic}
              filterCriteria={filterCriteria}
            />
          </div>
        </div>
      ),
      title: "Job Satisfaction",
      ref: jobSatisfactionRef,
    },
    {
      heatmap: (
        <Heatmap4
          selectedDemographic={selectedDemographic}
          filterCriteria={filterCriteria}
        />
      ),
      score: (
        <div className="score-wrapper">
          <div className="score-title">Organizational Commitment</div>
          <div className="score-chart">
            <Score04
              selectedDemographic={selectedDemographic}
              filterCriteria={filterCriteria}
            />
          </div>
        </div>
      ),
      title: "Organizational Commitment",
      ref: organizationalCommitmentRef,
    },
  ];

  return (
    <div className="dashboard-container">
      <Header
        title="Detailed Scores by Demographic Category"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <div className="main-content">
        <NavigationSidebar
          className={
            isSidebarOpen
              ? "navigation-sidebar-open"
              : "navigation-sidebar-closed"
          }
          isOpen={isSidebarOpen}
        />
        <div className="cciscore-detail-content-wrapper">
          <FilterSidebar
            isOpen={isFilterSidebarOpen}
            className={
              isFilterSidebarOpen
                ? "filter-sidebar-open"
                : "filter-sidebar-closed"
            }
            filterCriteria={filterCriteria}
            filterOptions={filterData}
            handleFilterChange={handleFilterChange}
          />

          <div className="cciscore-detail-charts-wrapper">
            <div className="demographic-selector">
              <label>
                Select Demographic:
                <select
                  value={selectedDemographic}
                  onChange={(e) => setSelectedDemographic(e.target.value)}
                >
                  {FILTER_CONFIG.filter(
                    ({ fieldName }) => fieldName !== "demographic_category_10"
                  ).map(({ fieldName, displayName }) => (
                    <option key={fieldName} value={fieldName}>
                      {displayName}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Screenshot Capture Button */}
            <div className="screenshot-button-container">
              <button
                onClick={captureAllScreenshots}
                className="screenshot-button"
              >
                Capture Screenshots
              </button>
            </div>

            <div className="cciscore-detail-charts-container">
              {chartComponents.map((chart, index) => (
                <div
                  key={index}
                  ref={chart.ref}
                  className="cciscore-detail-chart-row"
                >
                  <div className="score">{chart.score}</div>
                  <div className="heatmap">{chart.heatmap}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default D06_Detailed_Scores_by_Demographic_CategoryDashboard;